import { Component, OnInit, ViewEncapsulation, Output, EventEmitter } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DestructorComponent } from 'src/app/components/destructor/destructor.component';
import { CourierCompanyService } from 'src/app/services/courier-company/courier-company.service';
import { CourierCompanyModalService } from 'src/app/services/courier-company-modal/courier-company-modal.service';
import Swal from 'sweetalert2';

declare var bootstrap: any;

@Component({
  selector: 'app-add-edit-courier-company',
  templateUrl: './add-edit-courier-company.component.html',
  styleUrls: ['./add-edit-courier-company.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class AddEditCourierCompanyComponent extends DestructorComponent implements OnInit {

  @Output() courierCompanyAdded = new EventEmitter<void>();
  @Output() courierCompanyUpdated = new EventEmitter<void>();

  courierCompanyForm!: FormGroup;
  isLoading: boolean = false;
  isEditMode: boolean = false;
  selectedCourierCompany: any = null;

  constructor(
    private fb: FormBuilder,
    private _courierCompanyService: CourierCompanyService,
    private _courierCompanyModalService: CourierCompanyModalService
  ) {
    super();
    this.initializeForm();
  }

  ngOnInit(): void {
    this.setupModalListener();
  }

  private initializeForm(): void {
    this.courierCompanyForm = this.fb.group({
      company_name: ['', [Validators.required, Validators.minLength(2)]],
      start_url: ['', [Validators.required]],
      end_url: ['', [Validators.required]]
    });
  }

  private setupModalListener(): void {
    const modalElement = document.getElementById('kt_modal_courier_company_add_edit');
    if (modalElement) {
      modalElement.addEventListener('shown.bs.modal', () => {
        const serviceCourierCompany = this._courierCompanyModalService.getSelectedCourierCompany();
        if (serviceCourierCompany) {
          this.isEditMode = true;
          this.selectedCourierCompany = serviceCourierCompany;
          this.populateForm(this.selectedCourierCompany);
        } else {
          this.isEditMode = false;
          this.selectedCourierCompany = null;
          this.resetForm();
        }
      });

      modalElement.addEventListener('hidden.bs.modal', () => {
        this._courierCompanyModalService.clearSelectedCourierCompany();
        this.resetForm();
      });
    }
  }

  private populateForm(courierCompany: any): void {
    this.courierCompanyForm.patchValue({
      company_name: courierCompany.company_name || '',
      start_url: courierCompany.start_url || '',
      end_url: courierCompany.end_url || ''
    });
  }

  private resetForm(): void {
    this.courierCompanyForm.reset();
    this.isLoading = false;
    this.isEditMode = false;
    this.selectedCourierCompany = null;
  }

  // Getter methods for form controls
  get company_name() { return this.courierCompanyForm.get('company_name'); }
  get start_url() { return this.courierCompanyForm.get('start_url'); }
  get end_url() { return this.courierCompanyForm.get('end_url'); }

  closeModal(): void {
    const modalElement = document.getElementById('kt_modal_courier_company_add_edit');
    if (modalElement) {
      const modal = bootstrap.Modal.getInstance(modalElement);
      if (modal) {
        modal.hide();
      }
    }
  }

  onSubmit(): void {
    if (this.courierCompanyForm.invalid) {
      this.markFormGroupTouched(this.courierCompanyForm);
      return;
    }

    this.isLoading = true;

    const formData = {
      company_name: this.courierCompanyForm.get('company_name')?.value,
      start_url: this.courierCompanyForm.get('start_url')?.value,
      end_url: this.courierCompanyForm.get('end_url')?.value
    };

    if (this.isEditMode) {
      this.updateCourierCompany(formData);
    } else {
      this.addCourierCompany(formData);
    }
  }

  private addCourierCompany(formData: any): void {
    this.subscriptions.push(
      this._courierCompanyService.AddData(formData).subscribe({
        next: (response: any) => {
          this.isLoading = false;
          if (response.status) {
            Swal.fire({
              title: 'Success!',
              text: 'Courier company added successfully.',
              icon: 'success',
              confirmButtonText: 'OK'
            }).then(() => {
              this.closeModal();
              this.courierCompanyAdded.emit();
            });
          } else {
            Swal.fire({
              title: 'Error!',
              text: response.message || 'Failed to add courier company.',
              icon: 'error',
              confirmButtonText: 'OK'
            });
          }
        },
        error: (error: any) => {
          this.isLoading = false;
          console.error('Error adding courier company:', error);
          Swal.fire({
            title: 'Error!',
            text: error.error?.message || 'An error occurred while adding the courier company.',
            icon: 'error',
            confirmButtonText: 'OK'
          });
        }
      })
    );
  }

  private updateCourierCompany(formData: any): void {
    const courierCompanyId = this.selectedCourierCompany._id || this.selectedCourierCompany.id;
    this.subscriptions.push(
      this._courierCompanyService.UpdateData(courierCompanyId, formData).subscribe({
        next: (response: any) => {
          this.isLoading = false;
          if (response.status) {
            Swal.fire({
              title: 'Success!',
              text: 'Courier company updated successfully.',
              icon: 'success',
              confirmButtonText: 'OK'
            }).then(() => {
              this.closeModal();
              this.courierCompanyUpdated.emit();
            });
          } else {
            Swal.fire({
              title: 'Error!',
              text: response.message || 'Failed to update courier company.',
              icon: 'error',
              confirmButtonText: 'OK'
            });
          }
        },
        error: (error: any) => {
          this.isLoading = false;
          console.error('Error updating courier company:', error);
          Swal.fire({
            title: 'Error!',
            text: error.error?.message || 'An error occurred while updating the courier company.',
            icon: 'error',
            confirmButtonText: 'OK'
          });
        }
      })
    );
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      control?.markAsTouched();

      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      }
    });
  }

}
